/**
 * HTML最適化ユーティリティ
 * HTMLをLLMにとって理解しやすく、トークン効率の良い形に変換する
 */

export function optimizeHtmlForLLM(htmlText: string): string {
  const parser = new DOMParser()
  const doc = parser.parseFromString(htmlText, 'text/html')

  const parserError = doc.querySelector('parsererror')
  if (parserError) {
    throw new Error('HTML parsing failed: ' + parserError.textContent)
  }

  const body = doc.body || doc.documentElement
  processElement(body)

  return body.innerHTML
}

function processElement(element: Element): void {
  const children = Array.from(element.children)

  for (const child of children) {
    if (shouldRemoveElement(child)) {
      child.remove()
      continue
    }

    if (child.tagName.toLowerCase() === 'svg') {
      replaceSvgWithText(child)
      continue
    }

    // 属性の最適化（class のサニタイズを含む）
    optimizeAttributes(child)

    // 再帰
    processElement(child)
  }
}

function shouldRemoveElement(element: Element): boolean {
  const tagName = element.tagName.toLowerCase()

  const removeTags = ['script', 'style', 'noscript', 'canvas', 'iframe', 'video', 'audio', 'link']
  if (removeTags.includes(tagName)) return true

  if (tagName === 'link') {
    const rel = (element.getAttribute('rel') || '').toLowerCase()
    return rel === 'stylesheet' || rel === 'preload' || rel === 'prefetch'
  }

  return false
}

function replaceSvgWithText(svgElement: Element): void {
  const description = getSvgDescription(svgElement)
  const span = svgElement.ownerDocument!.createElement('span')
  span.textContent = `[SVG画像: ${description}]`
  svgElement.parentNode?.replaceChild(span, svgElement)
}

function getSvgDescription(svgElement: Element): string {
  const titleElement = svgElement.querySelector('title')
  if (titleElement?.textContent?.trim()) return titleElement.textContent.trim()

  const ariaLabel = svgElement.getAttribute('aria-label')
  if (ariaLabel?.trim()) return ariaLabel.trim()

  const className = svgElement.getAttribute('class')
  if (className) {
    const iconMatch = className.match(/(?:icon|fa|material)-([a-zA-Z-]+)/)
    if (iconMatch?.[1]) return iconMatch[1].replace(/-/g, ' ')
  }

  const id = svgElement.getAttribute('id')
  if (id) {
    const iconMatch = id.match(/(?:icon|fa|material)-([a-zA-Z-]+)/)
    if (iconMatch?.[1]) return iconMatch[1].replace(/-/g, ' ')
  }

  return 'アイコン'
}

/**
 * ▼▼ ここから追加/変更ポイント ▼▼
 */

// デザイン・ユーティリティ系クラスの代表的パターン
const FRAMEWORK_CLASS_PATTERNS: RegExp[] = [
  // CSS-in-JS や MUI
  /^css-[a-z0-9]+$/, // emotion/linaria等のハッシュ
  /^Mui[A-Z][\w-]*/, // MUI
  // Chakra / Mantine / Ant Design
  /^(chakra|mantine)-[\w-]+/,
  /^ant-[\w-]+/,
  // Bootstrap（構造名/ユーティリティ名を広くカバー）
  /^(?:container|row|col)(?:-[\w-]+)?$/,
  /^(?:btn|alert|badge|dropdown|navbar|card|modal|offcanvas|spinner)(?:-[\w-]+)?$/,
  /^(?:g|gx|gy|p|pt|pb|ps|pe|m|mt|mb|ms|me|w|h|vh|vw|minw|minh|maxw|maxh|text|bg|border|rounded|shadow|d|position|top|bottom|start|end|z|order|float|clearfix|ratio)(?:-[\w:]+)?$/,
  // Tailwind（= shadcn の実体）。プリフィクス/ユーティリティ名/レスポンシブ/状態修飾子も含む
  /^(?:sm|md|lg|xl|2xl):/,
  /^(?:hover|focus|active|disabled|visited|first|last|odd|even|checked|focus-visible|focus-within):/,
  /^(?:flex|grid|inline|block|hidden|contents|table|sr-only|static|relative|absolute|fixed|sticky)(?:-[\w:]+)?$/,
  /^(?:items|justify|content|place|self|col|row|gap|space|object|overflow|whitespace|break|truncate|inset|translate|rotate|skew|scale|origin)(?:-[\w:]+)?$/,
  /^(?:m|p|w|h|min-w|min-h|max-w|max-h|aspect|z|order|grow|shrink|basis|rounded|ring|shadow|opacity|blend|filter|backdrop|blur|brightness|contrast|grayscale|hue|invert|saturate|sepia|drop|stroke|fill|text|font|leading|tracking|list|decoration|underline|align)(?:-[\w:/]+)?$/,
]

// 意味・状態を表す可能性が高い class（保持候補）
const SEMANTIC_CLASS_ALLOWLIST = new Set([
  'active',
  'selected',
  'current',
  'visited',
  'disabled',
  'required',
  'invalid',
  'valid',
  'error',
  'warning',
  'success',
  'open',
  'closed',
  'expanded',
  'collapsed',
  // よく使われる汎用セマンティック
  'breadcrumb',
  'pagination',
  'nav',
  'menu',
  'submenu',
  'tab',
  'tablist',
  'tabpanel',
])

// Microformats（意味的な class を積極的に保持）
function isMicroformatsClass(token: string): boolean {
  return /^(h|p|u|dt|e)-/.test(token)
}

function isFrameworkOrUtilityClass(token: string): boolean {
  return FRAMEWORK_CLASS_PATTERNS.some((re) => re.test(token))
}

function shouldKeepClassToken(token: string): boolean {
  // 完全一致のホワイトリスト or Microformats は保持
  if (SEMANTIC_CLASS_ALLOWLIST.has(token)) return true
  if (isMicroformatsClass(token)) return true
  // 上記以外は原則削除（= LLM に不要な視覚/実装詳細）
  return false
}

function sanitizeClassAttribute(el: Element): void {
  const raw = el.getAttribute('class')
  if (!raw) return

  const tokens = raw
    .split(/\s+/)
    .map((t) => t.trim())
    .filter(Boolean)

  // 1) フレームワーク/ユーティリティ系は削除
  // 2) それ以外でも、ホワイトリスト/Microformats 以外は削除（= 原則捨てる）
  const kept = tokens.filter((t) => !isFrameworkOrUtilityClass(t) && shouldKeepClassToken(t))

  if (kept.length > 0) {
    el.setAttribute('class', kept.join(' '))
  } else {
    el.removeAttribute('class')
  }
}

/**
 * 要素の属性を最適化（不要な属性を削除）
 */
function optimizeAttributes(element: Element): void {
  const attributesToRemove = [
    // スタイリング関連
    'style',
    'width',
    'height',
    'color',
    'bgcolor',
    'background',
    'border',
    'cellpadding',
    'cellspacing',
    'align',
    'valign',

    // スクリプティング関連
    'onclick',
    'onmouseover',
    'onmouseout',
    'onload',
    'onfocus',
    'onblur',
    'onchange',
    'onsubmit',
    'onkeydown',
    'onkeyup',
    'onkeypress',

    // その他の技術的属性
    'data-toggle',
    'data-target',
    'data-dismiss',
    'autocomplete',
    'spellcheck',
    'contenteditable',
    'draggable',
    'hidden',
  ]

  for (const attr of attributesToRemove) {
    element.removeAttribute(attr)
  }

  // data-* の削除（意味のあるものは保持）
  const attributes = Array.from(element.attributes)
  for (const attr of attributes) {
    if (attr.name.startsWith('data-') && !isMeaningfulDataAttribute(attr.name)) {
      element.removeAttribute(attr.name)
    }
  }

  // class のサニタイズ（★ 追加）
  sanitizeClassAttribute(element)

  // id は基本削除。ただし重要なものは保持
  if (element.hasAttribute('id') && !isImportantId(element)) {
    element.removeAttribute('id')
  }

  // 注意: aria-* と role はセマンティクスに有益なので保持
}

function isMeaningfulDataAttribute(attributeName: string): boolean {
  const meaningfulDataAttrs = ['data-label', 'data-title', 'data-description', 'data-content', 'data-value', 'data-name', 'data-text']
  return meaningfulDataAttrs.some((m) => attributeName.toLowerCase().includes(m.toLowerCase()))
}

function isImportantId(element: Element): boolean {
  const tagName = element.tagName.toLowerCase()
  if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'section', 'article', 'main'].includes(tagName)) {
    return true
  }

  const id = element.getAttribute('id')
  if (id && element.ownerDocument) {
    const link = element.ownerDocument.querySelector(`a[href="#${id}"]`)
    return link !== null
  }
  return false
}

/**
 * セマンティック要素かどうか
 */
export function isSemanticElement(tagName: string): boolean {
  const semanticTags = [
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'p',
    'ul',
    'ol',
    'li',
    'blockquote',
    'strong',
    'em',
    'b',
    'i',
    'u',
    'mark',
    'small',
    'del',
    'ins',
    'sub',
    'sup',
    'pre',
    'code',
    'kbd',
    'samp',
    'var',
    'main',
    'article',
    'section',
    'nav',
    'header',
    'footer',
    'aside',
    'figure',
    'figcaption',
    'details',
    'summary',
    'a',
    'img',
    'table',
    'thead',
    'tbody',
    'tfoot',
    'tr',
    'th',
    'td',
    'caption',
    'colgroup',
    'col',
    'form',
    'fieldset',
    'legend',
    'label',
    'audio',
    'video',
    'track',
    'source',
    'address',
    'time',
    'abbr',
    'dfn',
    'cite',
    'q',
    'ruby',
    'rt',
    'rp',
  ]
  return semanticTags.includes(tagName.toLowerCase())
}
